import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Clock, Users, Download, ChevronRight, Flame } from 'lucide-react';

export const RecipeCard = ({ 
  recipe, 
  planId,
  day,
  mealType,
  onViewDetails,
  onDownloadPdf,
  compact = false 
}) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const nutrition = recipe?.nutrition;

  const handleDownload = async () => {
    const token = localStorage.getItem('auth_token');
    try {
      const response = await fetch(
        `${API_URL}/api/diet-plans/${planId}/recipe-pdf/${day}/${mealType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${recipe.name.replace(/\s+/g, '_')}_recipe.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download failed:', error);
    }
  };

  if (compact) {
    return (
      <div 
        className="p-4 rounded-xl border border-border bg-card hover:shadow-md transition-shadow cursor-pointer"
        onClick={onViewDetails}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onViewDetails?.()}
        data-testid={`recipe-card-compact-${mealType}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <Badge variant="outline" className="text-xs mb-2">
              {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
            </Badge>
            <h4 className="font-medium text-card-foreground">{recipe.name}</h4>
            <p className="text-sm text-muted-foreground mt-1">{recipe.description}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`recipe-card-${mealType}`}>
      <div 
        className="h-40 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center"
        aria-hidden="true"
      >
        <span className="text-6xl opacity-30">🍽️</span>
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="text-xs">
            {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
          </Badge>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" aria-hidden="true" />
              {recipe.prep_time}
            </span>
          </div>
        </div>
        <h3 className="font-heading text-xl font-semibold mt-2 text-card-foreground">
          {recipe.name}
        </h3>
      </CardHeader>

      <CardContent>
        <p className="text-muted-foreground text-sm">{recipe.description}</p>
        
        {/* Nutrition Quick View */}
        {nutrition && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs gap-1">
              <Flame className="h-3 w-3" aria-hidden="true" />
              {nutrition.calories} cal
            </Badge>
            <Badge variant="outline" className="text-xs">
              {nutrition.protein_g}g protein
            </Badge>
            <Badge variant="outline" className="text-xs">
              {nutrition.carbs_g}g carbs
            </Badge>
            <Badge variant="outline" className="text-xs">
              {nutrition.fat_g}g fat
            </Badge>
          </div>
        )}
        
        <div className="mt-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Ingredients ({recipe.ingredients?.length || 0})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recipe.ingredients?.slice(0, 5).map((ing, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {ing.name}
              </Badge>
            ))}
            {recipe.ingredients?.length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{recipe.ingredients.length - 5} more
              </Badge>
            )}
          </div>
        </div>
      </CardContent>

      <CardFooter className="gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={onViewDetails}
          data-testid={`view-recipe-${mealType}`}
        >
          View Recipe
        </Button>
        <Button 
          size="sm" 
          className="gap-2"
          onClick={handleDownload}
          data-testid={`download-pdf-${mealType}`}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          PDF
        </Button>
      </CardFooter>
    </Card>
  );
};
